import { combineReducers } from "redux";
import { getProvincias } from "../../components/Dashboard/DeclarationSummary/ProvinciasModal";

const initialUserState = {
  userData: {
    id: 45,
    companyName: "Atlantis Digital",
    address: "Kinderhuissingel 6a 2013 AS Haarlem",
    taxId: "123-45-6789",
    eori: "EORI123456",
    city: "Haarlem",
    postalCode: "12345",
    countryCode: "NL",
    customsOffice: "Atlantis Digital Office",
  },
  selectedSender: [], // Array of senders, one per export index
  selectedReceiver: [], // Array of receivers, one per export index
  selectedAgent: [], // Array of agents, one per export index
};

const initialCompanyState = {
  nombre: null,
  nif: null,
  eori: null,
  direccion: null,
  localidad: null,
  pais: null,
  codigo_postal: null,
  aduana_habitual: null,
  correo_electronico: null,
  needsCompanyCreation: false,
};

// Company reducer
const companyReducer = (state = initialCompanyState, action) => {
  switch (action.type) {
    case "SET_COMPANY":
      return { ...action.payload, needsCompanyCreation: false };
    case "UPDATE_COMPANY":
      return { ...state, ...action.payload };
    case "SET_NEEDS_COMPANY_CREATION":
      return { ...state, needsCompanyCreation: action.payload };
    default:
      return state;
  }
};

// User reducer
const userReducer = (state = initialUserState, action) => {
  switch (action.type) {
    case "UPDATE_USER":
      return { ...state, userData: action.payload };
    case "SET_SELECTED_SENDER":
      // action.payload should be { index: number, sender: object }
      if (action.payload && typeof action.payload.index === 'number') {
        const newSenders = [...(state.selectedSender || [])];
        newSenders[action.payload.index] = action.payload.sender;
        return { ...state, selectedSender: newSenders };
      }
      // Backward compatibility: if payload is just the sender object, treat as index 0
      const newSendersCompat = [...(state.selectedSender || [])];
      newSendersCompat[0] = action.payload;
      return { ...state, selectedSender: newSendersCompat };
    case "SET_SELECTED_RECEIVER":
      if (action.payload && typeof action.payload.index === 'number') {
        const newReceivers = [...(state.selectedReceiver || [])];
        newReceivers[action.payload.index] = action.payload.receiver;
        return { ...state, selectedReceiver: newReceivers };
      }
      const newReceiversCompat = [...(state.selectedReceiver || [])];
      newReceiversCompat[0] = action.payload;
      return { ...state, selectedReceiver: newReceiversCompat };
    case "SET_SELECTED_AGENT":
      if (action.payload && typeof action.payload.index === 'number') {
        const newAgents = [...(state.selectedAgent || [])];
        newAgents[action.payload.index] = action.payload.agent;
        return { ...state, selectedAgent: newAgents };
      }
      const newAgentsCompat = [...(state.selectedAgent || [])];
      newAgentsCompat[0] = action.payload;
      return { ...state, selectedAgent: newAgentsCompat };
    case "RESET_SELECTED_SENDER":
      return { ...state, selectedSender: [] };
    case "RESET_SELECTED_RECEIVER":
      return { ...state, selectedReceiver: [] };
    case "RESET_SELECTED_AGENT":
      return { ...state, selectedAgent: [] };
    default:
      return state;
  }
};

// Agents reducer
export const agentsReducer = (state = [], action) => {
  switch (action.type) {
    case "SET_AGENTS":
      // If payload is already an array, use it directly
      if (Array.isArray(action.payload)) {
        return action.payload;
      }
      // If payload has a data property that's an array, use that
      else if (action.payload && Array.isArray(action.payload.data)) {
        return action.payload.data;
      }
      // Otherwise return the current state (or empty array if needed)
      return state;

    case "ADD_AGENT":
      if (action.payload) {
        return [...state, action.payload];
      }
      return state;

    case "UPDATE_AGENT":
      if (action.payload) {
        return state.map((agent) =>
          agent.id === action.payload.id ? action.payload : agent
        );
      }
      return state;

    case "DELETE_AGENT":
      return state.filter((agent) => agent.id !== action.payload);

    default:
      return state;
  }
};

// Senders reducer
export const sendersReducer = (state = [], action) => {
  switch (action.type) {
    case "SET_SENDERS":
      // If payload is already an array, use it directly
      if (Array.isArray(action.payload)) {
        return action.payload;
      }
      // If payload has a data property that's an array, use that
      else if (action.payload && Array.isArray(action.payload.data)) {
        return action.payload.data;
      }
      // Otherwise return the current state (or empty array if needed)
      return state;

    case "ADD_SENDER":
      if (action.payload) {
        return [...state, action.payload];
      }
      return state;

    case "UPDATE_SENDER":
      if (action.payload) {
        return state.map((sender) =>
          sender.id === action.payload.id ? action.payload : sender
        );
      }
      return state;

    case "DELETE_SENDER":
      return state.filter((sender) => sender.id !== action.payload);

    default:
      return state;
  }
};

// Receivers reducer
export const receiversReducer = (state = [], action) => {
  switch (action.type) {
    case "SET_RECEIVERS":
      // If payload is already an array, use it directly
      if (Array.isArray(action.payload)) {
        return action.payload;
      }
      // If payload has a data property that's an array, use that
      else if (action.payload && Array.isArray(action.payload.data)) {
        return action.payload.data;
      }
      // Otherwise return the current state (or empty array if needed)
      return state;

    case "ADD_RECEIVER":
      if (action.payload) {
        return [...state, action.payload];
      }
      return state;

    case "UPDATE_RECEIVER":
      if (action.payload) {
        return state.map((receiver) =>
          receiver.id === action.payload.id ? action.payload : receiver
        );
      }
      return state;

    case "DELETE_RECEIVER":
      return state.filter((receiver) => receiver.id !== action.payload);

    default:
      return state;
  }
};

// Grouped Taric JSON Outputs reducer
export const groupedTaricJsonOutputsReducer = (state = [], action) => {
  switch (action.type) {
    case "SET_GROUPED_TARIC_JSON_OUTPUTS":
      // If payload is already an array, use it directly
      if (Array.isArray(action.payload)) {
        return action.payload;
      }
      // If payload has a data property that's an array, use that
      else if (action.payload && Array.isArray(action.payload.data)) {
        return action.payload.data;
      }
      // Otherwise return the current state (or empty array if needed)
      return state;
    default:
      return state;
  }
};

// Action Types
export const ADD_DOCUMENTS = "ADD_DOCUMENTS";
export const REMOVE_DOCUMENT = "REMOVE_DOCUMENT";
export const CLEAR_DOCUMENTS = "CLEAR_DOCUMENTS";
export const UPLOAD_COMPLETE = "UPLOAD_COMPLETE";
export const RESET_UPLOAD_STATUS = "RESET_UPLOAD_STATUS";
export const SET_UPLOADED_FILES = "SET_UPLOADED_FILES";
export const SET_STEPS_COMPLETED = "SET_STEPS_COMPLETED";
export const RESET_DOCUMENTS_STATE = "RESET_DOCUMENTS_STATE";
export const SET_EXTRACTED_DATA = "SET_EXTRACTED_DATA";
export const SET_EDITABLE_DATA = "SET_EDITABLE_DATA";
export const FILTER_DECLARATIONS_BY_ERRORS = "FILTER_DECLARATIONS_BY_ERRORS";
export const TOGGLE_COLLAPSE_FILES = "TOGGLE_COLLAPSE_FILES";

// New action types for declaration editing
export const UPDATE_DECLARATION_FIELD = "UPDATE_DECLARATION_FIELD";
export const UPDATE_DOCUMENT_FIELD = "UPDATE_DOCUMENT_FIELD";
export const UPDATE_ITEM_FIELD = "UPDATE_ITEM_FIELD";
export const SAVE_DECLARATION_CHANGES = "SAVE_DECLARATION_CHANGES";
export const SET_DECLARATION_DIRTY = "SET_DECLARATION_DIRTY";
export const SET_LAST_EDITED_DOCUMENT_FIELD = "SET_LAST_EDITED_DOCUMENT_FIELD";
export const CLEAR_LAST_EDITED_DOCUMENT_FIELD = "CLEAR_LAST_EDITED_DOCUMENT_FIELD";

// Add new action types for selected declaration
export const SET_SELECTED_DECLARATION = "SET_SELECTED_DECLARATION";
export const CLEAR_SELECTED_DECLARATION = "CLEAR_SELECTED_DECLARATION";

// Initial state
const initialDocumentsState = {
  documents: [],
  hasDocuments: false,
  uploadComplete: false,
  uploadedFiles: [],
  stepsCompleted: false,
  extractedData: null,
  editableData: null, // Store editable data in Redux so it's shared across components
  isDirty: false,
  collapseFiles: false,
  lastEditedDocumentField: null, // Track last edited document field for "Apply to All"
  lastEditedDocumentValue: null, // Track last edited document value for "Apply to All"
  lastEditedDocumentDeclarationIndex: null,
  lastEditedDocumentPartidaIndex: null,
  lastEditedDocumentDocumentIndex: null,
  selectedTemplate: null, // Selected template for styling and data
};

// Helper function to create a deep copy of nested arrays/objects
const deepCopy = (obj) => {
  try {
    if (obj === null || obj === undefined) {
      return obj;
    }
    return JSON.parse(JSON.stringify(obj));
  } catch (error) {
    console.error('deepCopy: Error copying object:', error, 'Object:', obj);
    // Return a shallow copy as fallback
    if (Array.isArray(obj)) {
      return [...obj];
    } else if (typeof obj === 'object') {
      return { ...obj };
    }
    return obj;
  }
};

// Documents reducer with declaration editing functionality
const documentsReducer = (state = initialDocumentsState, action) => {
  switch (action.type) {
    case ADD_DOCUMENTS:
      return {
        ...state,
        documents: [...state.documents, ...action.payload],
        hasDocuments: true,
      };

    case REMOVE_DOCUMENT:
      const updatedDocuments = state.documents.filter(
        (doc) => doc.name !== action.payload
      );
      return {
        ...state,
        documents: updatedDocuments,
        hasDocuments: updatedDocuments.length > 0,
      };

    case CLEAR_DOCUMENTS:
      return {
        ...state,
        documents: [],
        hasDocuments: false,
        uploadComplete: false,
        uploadedFiles: [],
      };

    case UPLOAD_COMPLETE:
      return {
        ...state,
        uploadComplete: true,
      };

    case RESET_UPLOAD_STATUS:
      return {
        ...state,
        uploadComplete: false,
      };

    case SET_UPLOADED_FILES:
      return {
        ...state,
        uploadedFiles: action.payload,
      };

    case SET_STEPS_COMPLETED:
      return {
        ...state,
        stepsCompleted: action.payload,
      };

    case SET_EXTRACTED_DATA:
      // Handle null payload - clear extractedData and editableData
      if (action.payload === null || action.payload === undefined) {
        return {
          ...state,
          extractedData: null,
          editableData: null,
          isDirty: false,
        };
      }

      // Validate payload structure with fallbacks
      let validPayload = null;
      
      if (Array.isArray(action.payload)) {
        // Filter out null/undefined/empty objects
        const filteredPayload = action.payload.filter(d => 
          d !== null && 
          d !== undefined && 
          typeof d === 'object' && 
          Object.keys(d).length > 0
        );
        if (filteredPayload.length > 0) {
          validPayload = filteredPayload;
        }
      } else if (action.payload && typeof action.payload === 'object' && Object.keys(action.payload).length > 0) {
        // Single object, convert to array
        validPayload = [action.payload];
      }
      
      // Fallback: If payload is invalid, try to use existing extractedData or return current state
      if (!validPayload || validPayload.length === 0) {
        console.warn('SET_EXTRACTED_DATA: Invalid payload, keeping existing data or clearing');
        // If we have existing data, keep it; otherwise clear
        if (state.extractedData && Array.isArray(state.extractedData) && state.extractedData.length > 0) {
          return state; // Keep existing data
        }
        return {
          ...state,
          extractedData: null,
          editableData: null,
          isDirty: false,
        };
      }

      // Generate incremented expediente values starting from CF00001
      const generateExpedienteForExtracted = (index) => {
        const number = (index + 1).toString().padStart(5, '0');
        return `CF${number}`;
      };

      // Check if this is initial extraction (state.extractedData is null/empty) or an update
      const isInitialExtraction = !state.extractedData || 
        (Array.isArray(state.extractedData) && state.extractedData.length === 0);

      // Helper function to check if expediente is in our generated format (CF00001, CF00002, etc.)
      const isGeneratedExpediente = (expediente) => {
        if (!expediente) return false;
        return /^CF\d{5}$/.test(expediente);
      };

      // Process extracted data to add incremented expediente values
      // For initial extraction: always generate new expediente values (ignore API values)
      // For updates: preserve expediente if it's in our format (CF00001), otherwise generate new
      // Use validPayload instead of action.payload
      const processedExtractedData = validPayload.map((declaration, index) => {
        // Validate declaration structure
        if (!declaration || typeof declaration !== 'object') {
          console.warn(`SET_EXTRACTED_DATA: Invalid declaration at index ${index}, skipping`);
          return null;
        }
        return {
          ...declaration,
          expediente: isInitialExtraction 
            ? generateExpedienteForExtracted(index)  // Always generate for initial extraction
            : (isGeneratedExpediente(declaration?.expediente) 
                ? declaration.expediente  // Preserve if already in our format
                : generateExpedienteForExtracted(index))  // Generate if not in our format
        };
      }).filter(d => d !== null); // Remove any null entries from invalid declarations

      // Check if expediente values already exist in our format (indicating this is an update, not new data)
      const hasExistingExpediente = processedExtractedData.some(decl => isGeneratedExpediente(decl?.expediente));

      // Final validation: ensure processedExtractedData is valid
      if (!processedExtractedData || processedExtractedData.length === 0) {
        console.warn('SET_EXTRACTED_DATA: processedExtractedData is empty after processing');
        return state; // Keep existing state if processing failed
      }

      return {
        ...state,
        extractedData: validPayload, // Use validated payload
        editableData: processedExtractedData, // Set editableData with incremented expediente values
        isDirty: hasExistingExpediente ? state.isDirty : false, // Only reset dirty flag for new data
      };

    case SET_EDITABLE_DATA:
      // Validate payload before setting
      let validEditableData = null;
      
      if (action.payload === null || action.payload === undefined) {
        validEditableData = null;
      } else if (Array.isArray(action.payload)) {
        // Filter out null/undefined/empty objects
        const filtered = action.payload.filter(d => 
          d !== null && 
          d !== undefined && 
          typeof d === 'object' && 
          Object.keys(d).length > 0
        );
        validEditableData = filtered.length > 0 ? filtered : null;
      } else if (action.payload && typeof action.payload === 'object' && Object.keys(action.payload).length > 0) {
        validEditableData = [action.payload];
      }
      
      // If invalid, keep existing editableData or set to null
      if (validEditableData === null && action.payload !== null) {
        console.warn('SET_EDITABLE_DATA: Invalid payload, keeping existing editableData');
        return state;
      }
      
      return {
        ...state,
        editableData: validEditableData,
      };

    case FILTER_DECLARATIONS_BY_ERRORS:
      // Filter extractedData and editableData to keep only declarations with errors
      const errorIndices = action.payload; // Array of indices to keep
      let filteredExtractedData = state.extractedData;
      let filteredEditableData = state.editableData;
      
      // Helper function to generate expediente
      const generateExpediente = (index) => {
        const number = (index + 1).toString().padStart(5, '0');
        return `CF${number}`;
      };
      
      if (Array.isArray(state.extractedData) && errorIndices.length > 0) {
        filteredExtractedData = state.extractedData
          .filter((_, index) => errorIndices.includes(index))
          .map((declaration, newIndex) => ({
            ...declaration,
            expediente: generateExpediente(newIndex), // Regenerate expediente: CF00001, CF00002, etc.
          }));
      }
      
      if (Array.isArray(state.editableData) && errorIndices.length > 0) {
        filteredEditableData = state.editableData
          .filter((_, index) => errorIndices.includes(index))
          .map((declaration, newIndex) => ({
            ...declaration,
            expediente: generateExpediente(newIndex), // Regenerate expediente: CF00001, CF00002, etc.
          }));
      }
      
      return {
        ...state,
        extractedData: filteredExtractedData,
        editableData: filteredEditableData,
      };

    case RESET_DOCUMENTS_STATE:
      return initialDocumentsState;

    case "SET_SELECTED_TEMPLATE":
      return {
        ...state,
        selectedTemplate: action.payload,
      };

    case "CLEAR_SELECTED_TEMPLATE":
      return {
        ...state,
        selectedTemplate: null,
      };

    // New cases for declaration editing
    case UPDATE_DECLARATION_FIELD: {
      const { declarationIndex, field, value } = action.payload || {};

      // Validate payload
      if (declarationIndex === undefined || field === undefined) {
        console.warn('UPDATE_DECLARATION_FIELD: Invalid payload, missing declarationIndex or field');
        return state;
      }

      // Validate state.extractedData
      if (!state.extractedData) {
        console.warn('UPDATE_DECLARATION_FIELD: No extractedData in state');
        return state;
      }

      try {
        // Create a deep copy to avoid state mutation
        let updatedData = deepCopy(state.extractedData);

        if (Array.isArray(updatedData)) {
          // Validate array bounds
          if (declarationIndex < 0 || declarationIndex >= updatedData.length) {
            console.warn(`UPDATE_DECLARATION_FIELD: declarationIndex ${declarationIndex} out of bounds (length: ${updatedData.length})`);
            return state;
          }
          
          // Validate declaration exists
          if (!updatedData[declarationIndex] || typeof updatedData[declarationIndex] !== 'object') {
            console.warn(`UPDATE_DECLARATION_FIELD: Invalid declaration at index ${declarationIndex}`);
            return state;
          }
          
          updatedData[declarationIndex][field] = value;
        } else if (updatedData && typeof updatedData === 'object') {
          // Handle case where extractedData is a single object (not in array)
          updatedData[field] = value;
        } else {
          console.warn('UPDATE_DECLARATION_FIELD: extractedData is not an array or object');
          return state;
        }

        return {
          ...state,
          extractedData: updatedData,
          isDirty: true,
        };
      } catch (error) {
        console.error('UPDATE_DECLARATION_FIELD: Error updating field:', error);
        return state;
      }
    }

    case UPDATE_DOCUMENT_FIELD: {
      const { declarationIndex, documentIndex, field, value } = action.payload || {};

      // Validate payload
      if (declarationIndex === undefined || documentIndex === undefined || field === undefined) {
        console.warn('UPDATE_DOCUMENT_FIELD: Invalid payload, missing required fields');
        return state;
      }

      // Validate state.extractedData
      if (!state.extractedData) {
        console.warn('UPDATE_DOCUMENT_FIELD: No extractedData in state');
        return state;
      }

      try {
        // Create a deep copy
        let updatedData = deepCopy(state.extractedData);

        if (Array.isArray(updatedData)) {
          // Validate array bounds
          if (declarationIndex < 0 || declarationIndex >= updatedData.length) {
            console.warn(`UPDATE_DOCUMENT_FIELD: declarationIndex ${declarationIndex} out of bounds`);
            return state;
          }
          
          const declaration = updatedData[declarationIndex];
          if (!declaration || typeof declaration !== 'object') {
            console.warn(`UPDATE_DOCUMENT_FIELD: Invalid declaration at index ${declarationIndex}`);
            return state;
          }
          
          // Validate documentos array
          if (!declaration.documentos || !Array.isArray(declaration.documentos)) {
            console.warn(`UPDATE_DOCUMENT_FIELD: No documentos array in declaration ${declarationIndex}`);
            return state;
          }
          
          if (documentIndex < 0 || documentIndex >= declaration.documentos.length) {
            console.warn(`UPDATE_DOCUMENT_FIELD: documentIndex ${documentIndex} out of bounds`);
            return state;
          }
          
          const documento = declaration.documentos[documentIndex];
          if (!documento || typeof documento !== 'object') {
            console.warn(`UPDATE_DOCUMENT_FIELD: Invalid documento at index ${documentIndex}`);
            return state;
          }
          
          updatedData[declarationIndex].documentos[documentIndex][field] = value;
        } else if (updatedData && typeof updatedData === 'object') {
          // Handle single object case
          if (!updatedData.documentos || !Array.isArray(updatedData.documentos)) {
            console.warn('UPDATE_DOCUMENT_FIELD: No documentos array in extractedData');
            return state;
          }
          
          if (documentIndex < 0 || documentIndex >= updatedData.documentos.length) {
            console.warn(`UPDATE_DOCUMENT_FIELD: documentIndex ${documentIndex} out of bounds`);
            return state;
          }
          
          const documento = updatedData.documentos[documentIndex];
          if (!documento || typeof documento !== 'object') {
            console.warn(`UPDATE_DOCUMENT_FIELD: Invalid documento at index ${documentIndex}`);
            return state;
          }
          
          updatedData.documentos[documentIndex][field] = value;
        } else {
          console.warn('UPDATE_DOCUMENT_FIELD: extractedData is not an array or object');
          return state;
        }

        return {
          ...state,
          extractedData: updatedData,
          isDirty: true,
        };
      } catch (error) {
        console.error('UPDATE_DOCUMENT_FIELD: Error updating document field:', error);
        return state;
      }
    }

    case UPDATE_ITEM_FIELD: {
      const { declarationIndex, itemIndex, field, value } = action.payload || {};

      // Validate payload
      if (declarationIndex === undefined || itemIndex === undefined || field === undefined) {
        console.warn('UPDATE_ITEM_FIELD: Invalid payload, missing required fields');
        return state;
      }

      // Validate state.extractedData
      if (!state.extractedData) {
        console.warn('UPDATE_ITEM_FIELD: No extractedData in state');
        return state;
      }

      try {
        // Create a deep copy
        let updatedData = deepCopy(state.extractedData);

        if (Array.isArray(updatedData)) {
          // Validate array bounds
          if (declarationIndex < 0 || declarationIndex >= updatedData.length) {
            console.warn(`UPDATE_ITEM_FIELD: declarationIndex ${declarationIndex} out of bounds`);
            return state;
          }
          
          const declaration = updatedData[declarationIndex];
          if (!declaration || typeof declaration !== 'object') {
            console.warn(`UPDATE_ITEM_FIELD: Invalid declaration at index ${declarationIndex}`);
            return state;
          }
          
          // Validate partidas array
          if (!declaration.partidas || !Array.isArray(declaration.partidas)) {
            console.warn(`UPDATE_ITEM_FIELD: No partidas array in declaration ${declarationIndex}`);
            return state;
          }
          
          if (itemIndex < 0 || itemIndex >= declaration.partidas.length) {
            console.warn(`UPDATE_ITEM_FIELD: itemIndex ${itemIndex} out of bounds`);
            return state;
          }
          
          const item = declaration.partidas[itemIndex];
          if (!item || typeof item !== 'object') {
            console.warn(`UPDATE_ITEM_FIELD: Invalid item at index ${itemIndex}`);
            return state;
          }
          
          updatedData[declarationIndex].partidas[itemIndex][field] = value;
        } else if (updatedData && typeof updatedData === 'object') {
          // Handle single object case
          if (!updatedData.partidas || !Array.isArray(updatedData.partidas)) {
            console.warn('UPDATE_ITEM_FIELD: No partidas array in extractedData');
            return state;
          }
          
          if (itemIndex < 0 || itemIndex >= updatedData.partidas.length) {
            console.warn(`UPDATE_ITEM_FIELD: itemIndex ${itemIndex} out of bounds`);
            return state;
          }
          
          const item = updatedData.partidas[itemIndex];
          if (!item || typeof item !== 'object') {
            console.warn(`UPDATE_ITEM_FIELD: Invalid item at index ${itemIndex}`);
            return state;
          }
          
          updatedData.partidas[itemIndex][field] = value;
        } else {
          console.warn('UPDATE_ITEM_FIELD: extractedData is not an array or object');
          return state;
        }

        return {
          ...state,
          extractedData: updatedData,
          isDirty: true,
        };
      } catch (error) {
        console.error('UPDATE_ITEM_FIELD: Error updating item field:', error);
        return state;
      }
    }

    case SAVE_DECLARATION_CHANGES:
      // Validate payload before saving
      if (action.payload === null || action.payload === undefined) {
        console.warn('SAVE_DECLARATION_CHANGES: Invalid payload (null/undefined)');
        return state;
      }
      
      // Validate payload structure
      const isValidPayload = Array.isArray(action.payload) 
        ? action.payload.length > 0 && action.payload.every(d => d && typeof d === 'object')
        : action.payload && typeof action.payload === 'object' && Object.keys(action.payload).length > 0;
      
      if (!isValidPayload) {
        console.warn('SAVE_DECLARATION_CHANGES: Invalid payload structure');
        return state;
      }
      
      return {
        ...state,
        extractedData: action.payload,
        isDirty: false, // Reset dirty flag after saving
      };

    case SET_DECLARATION_DIRTY:
      return {
        ...state,
        isDirty: action.payload,
      };

    case SET_LAST_EDITED_DOCUMENT_FIELD:
      return {
        ...state,
        lastEditedDocumentField: action.payload.field,
        lastEditedDocumentValue: action.payload.value,
        lastEditedDocumentDeclarationIndex: action.payload.declarationIndex,
        lastEditedDocumentPartidaIndex: action.payload.partidaIndex,
        lastEditedDocumentDocumentIndex: action.payload.documentIndex,
      };

    case CLEAR_LAST_EDITED_DOCUMENT_FIELD:
      return {
        ...state,
        lastEditedDocumentField: null,
        lastEditedDocumentValue: null,
        lastEditedDocumentDeclarationIndex: null,
        lastEditedDocumentPartidaIndex: null,
        lastEditedDocumentDocumentIndex: null,
      };

    case TOGGLE_COLLAPSE_FILES:
      return {
        ...state,
        collapseFiles: !state.collapseFiles,
      };

    default:
      return state;
  }
};

// Action Creators
export const addDocuments = (files) => ({
  type: ADD_DOCUMENTS,
  payload: files,
});

export const removeDocument = (fileName) => ({
  type: REMOVE_DOCUMENT,
  payload: fileName,
});

export const clearDocuments = () => ({
  type: CLEAR_DOCUMENTS,
});

export const uploadComplete = () => ({
  type: UPLOAD_COMPLETE,
});

export const resetUploadStatus = () => ({
  type: RESET_UPLOAD_STATUS,
});

export const setUploadedFiles = (files) => ({
  type: SET_UPLOADED_FILES,
  payload: files,
});

export const setStepsCompleted = (completed) => ({
  type: SET_STEPS_COMPLETED,
  payload: completed,
});

export const resetDocumentsState = () => ({
  type: RESET_DOCUMENTS_STATE,
});

export const setExtractedData = (data) => ({
  type: SET_EXTRACTED_DATA,
  payload: data,
});

export const setEditableData = (data) => ({
  type: SET_EDITABLE_DATA,
  payload: data,
});

export const setSelectedTemplate = (template) => ({
  type: "SET_SELECTED_TEMPLATE",
  payload: template,
});

export const clearSelectedTemplate = () => ({
  type: "CLEAR_SELECTED_TEMPLATE",
});

// New action creators for declaration editing
export const updateDeclarationField = (declarationIndex, field, value) => ({
  type: UPDATE_DECLARATION_FIELD,
  payload: {
    declarationIndex,
    field,
    value,
  },
});

export const updateDocumentField = (
  declarationIndex,
  documentIndex,
  field,
  value
) => ({
  type: UPDATE_DOCUMENT_FIELD,
  payload: {
    declarationIndex,
    documentIndex,
    field,
    value,
  },
});

export const updateItemField = (declarationIndex, itemIndex, field, value) => ({
  type: UPDATE_ITEM_FIELD,
  payload: {
    declarationIndex,
    itemIndex,
    field,
    value,
  },
});

export const saveDeclarationChanges = (data) => ({
  type: SAVE_DECLARATION_CHANGES,
  payload: data,
});

export const setDeclarationDirty = (isDirty) => ({
  type: SET_DECLARATION_DIRTY,
  payload: isDirty,
});

export const setLastEditedDocumentField = (
  fieldOrPayload,
  value,
  declarationIndex,
  partidaIndex,
  documentIndex
) => {
  if (
    typeof fieldOrPayload === 'object' &&
    fieldOrPayload !== null &&
    !Array.isArray(fieldOrPayload)
  ) {
    return {
      type: SET_LAST_EDITED_DOCUMENT_FIELD,
      payload: fieldOrPayload,
    };
  }

  return {
    type: SET_LAST_EDITED_DOCUMENT_FIELD,
    payload: {
      field: fieldOrPayload,
      value,
      declarationIndex,
      partidaIndex,
      documentIndex,
    },
  };
};

export const clearLastEditedDocumentField = () => ({
  type: CLEAR_LAST_EDITED_DOCUMENT_FIELD,
});

export const setShowBackToDashboardButton = (show) => ({
  type: "SET_SHOW_BACK_TO_DASHBOARD_BUTTON",
  payload: show,
});

export const setSelectedSender = (sender, index = 0) => ({
  type: "SET_SELECTED_SENDER",
  payload: { index, sender },
});

export const setSelectedReceiver = (receiver, index = 0) => ({
  type: "SET_SELECTED_RECEIVER",
  payload: { index, receiver },
});

export const setSelectedAgent = (agent, index = 0) => ({
  type: "SET_SELECTED_AGENT",
  payload: { index, agent },
});

export const resetSelectedSender = () => ({
  type: "RESET_SELECTED_SENDER",
});

export const resetSelectedReceiver = () => ({
  type: "RESET_SELECTED_RECEIVER",
});

export const resetSelectedAgent = () => ({
  type: "RESET_SELECTED_AGENT",
});

const initialUIState = {
  showBacktoDashboardButton: false,
};

// Add new validation errors reducer
const initialValidationErrorsState = {
  errors: [],
  isSpanish: true
};

const validationErrorsReducer = (state = initialValidationErrorsState, action) => {
  switch (action.type) {
    case "SET_VALIDATION_ERRORS":
      return {
        ...state,
        errors: action.payload.errors,
        isSpanish: action.payload.isSpanish
      };
    case "CLEAR_VALIDATION_ERRORS":
      return {
        ...state,
        errors: [],
        isSpanish: state.isSpanish
      };
    case "SET_VALIDATION_LANGUAGE":
      return {
        ...state,
        isSpanish: action.payload
      };
    default:
      return state;
  }
};

const uiReducer = (state = initialUIState, action) => {
  switch (action.type) {
    case "SET_SHOW_BACK_TO_DASHBOARD_BUTTON":
      return { ...state, showBacktoDashboardButton: action.payload };
    default:
      return state;
  }
};

// Taric JSON preparation reducer
const initialPreparedJsonState = {
  cliente: null, // Client/importer/exporter details (backward compatibility)
  clientes: [], // Array of clients, one per export
  destinatario: null, // Receiver details (for exports)
  remitente: null, // Sender/shipper details (backward compatibility)
  remitentes: [], // Array of senders, one per export
  agente: null, // Customs agent details (backward compatibility)
  agentes: [], // Array of agents, one per export
  ocrInputID: "", // OCR Input ID
  taricJsonErrors: null, // Taric JSON validation errors

  // Declaration details
  expediente: null, // File reference
  tipo: null, // Declaration type (64 for imports, 32 for exports)
  aduana_entrada: null, // Customs office of entry (for imports)
  aduana_salida: null, // Customs office of exit (for exports)
  aduana_despacho: null, // Customs office of declaration
  fecha_registro: null, // Registration date

  // For exports
  pais_destino: null, // Country of destination
  pais_exportacion: null, // Country of export
  modo_transporte_frontera: null, // Border transport mode
  modo_transporte_interior: null, // Internal transport mode
  identidad_transporte_frontera: null, // Border transport identity
  pais_transporte_frontera: null, // Border transport country

  // Commercial Information
  tiene_contenedores: null, // Has containers (0/1)
  total_bultos: null, // Total packages
  factura: null, // Invoice number
  fecha_factura: null, // Invoice date
  condiciones_entrega: null, // Delivery terms
  naturaleza_transaccion: null, // Transaction nature

  // Items details
  partidas: [],

  // Declaration collections
  declarations: [],

  // State
  isImport: true, // Default to import, can toggle to export
  isReady: false,
  isSubmitted: false,
  submissionError: null,

  // Final structured JSON for Taric
  taricJson: null,
  
  // Selected exportaciones index for grouped Taric JSON outputs
  selectedExportacionesIndex: null,
  
  // Successful and error expedientes for modal display
  successfulExpedientes: [],
  errorExpedientes: [],
};

// preparedJsonReducer with updated field mapping
const preparedJsonReducer = (state = initialPreparedJsonState, action) => {
  switch (action.type) {
    case "SET_TARIC_JSON_ERRORS":
      console.log('SET_TARIC_JSON_ERRORS reducer - action.payload:', action.payload);
      console.log('SET_TARIC_JSON_ERRORS reducer - action.payload type:', typeof action.payload);
      console.log('SET_TARIC_JSON_ERRORS reducer - action.payload is array?', Array.isArray(action.payload));
      
      // Check if this is the new error format with errors array
      const isNewErrorFormat = action.payload && 
        typeof action.payload === 'object' && 
        !Array.isArray(action.payload) &&
        Array.isArray(action.payload.errors) &&
        action.payload.errors.length > 0;
      
      let filteredState = { ...state, taricJsonErrors: action.payload };
      
      // If new error format, filter declarations to keep only exports with errors
      if (isNewErrorFormat && state.declarations && state.declarations.length > 0) {
        // Extract exportacionesIndex values from errors
        const errorIndices = new Set(
          action.payload.errors
            .map(err => err.exportacionesIndex)
            .filter(idx => idx !== undefined && typeof idx === 'number' && idx >= 0)
        );
        
        console.log('Error indices found:', Array.from(errorIndices));
        console.log('Total declarations before filtering:', state.declarations.length);
        
        // Filter declarations to keep only those with errors
        let filteredDeclarations = state.declarations.filter((_, index) => errorIndices.has(index));
        
        // Re-index declarations and regenerate expediente values starting from CF00001
        const generateExpediente = (index) => {
          const number = (index + 1).toString().padStart(5, '0');
          return `CF${number}`;
        };
        
        filteredDeclarations = filteredDeclarations.map((declaration, newIndex) => ({
          ...declaration,
          expediente: generateExpediente(newIndex), // Regenerate expediente: CF00001, CF00002, etc.
        }));
        
        // Filter clientes, remitentes, agentes arrays to match
        const filteredClientes = state.clientes ? state.clientes.filter((_, index) => errorIndices.has(index)) : [];
        const filteredRemitentes = state.remitentes ? state.remitentes.filter((_, index) => errorIndices.has(index)) : [];
        const filteredAgentes = state.agentes ? state.agentes.filter((_, index) => errorIndices.has(index)) : [];
        
        console.log('Filtered declarations count:', filteredDeclarations.length);
        console.log('Filtered clientes count:', filteredClientes.length);
        console.log('Filtered remitentes count:', filteredRemitentes.length);
        console.log('Filtered agentes count:', filteredAgentes.length);
        
        // Create a mapping from old exportacionesIndex to new index
        const oldToNewIndexMap = new Map();
        let newIndex = 0;
        for (let oldIndex = 0; oldIndex < state.declarations.length; oldIndex++) {
          if (errorIndices.has(oldIndex)) {
            oldToNewIndexMap.set(oldIndex, newIndex);
            newIndex++;
          }
        }
        
        // Transform errors to use new indices
        const transformedErrors = action.payload.errors.map(err => {
          const oldIndex = err.exportacionesIndex;
          const newIndex = oldToNewIndexMap.get(oldIndex);
          if (newIndex !== undefined) {
            return {
              ...err,
              exportacionesIndex: newIndex, // Update to new index
            };
          }
          return err;
        });
        
        // Update the error payload with transformed indices
        const transformedErrorPayload = {
          ...action.payload,
          errors: transformedErrors,
        };
        
        filteredState = {
          ...filteredState,
          taricJsonErrors: transformedErrorPayload, // Use transformed errors with new indices
          declarations: filteredDeclarations,
          clientes: filteredClientes,
          remitentes: filteredRemitentes,
          agentes: filteredAgentes,
          // Update backward compatibility fields
          cliente: filteredClientes.length > 0 ? filteredClientes[0] : null,
          remitente: filteredRemitentes.length > 0 ? filteredRemitentes[0] : null,
          agente: filteredAgentes.length > 0 ? filteredAgentes[0] : null,
        };
      }
      
      console.log('SET_TARIC_JSON_ERRORS reducer - new state taricJsonErrors:', filteredState.taricJsonErrors);
      return filteredState;

    case "CLEAR_TARIC_JSON_ERRORS":
      return {
        ...state,
        taricJsonErrors: null,
      };

    case "SET_OCR_INPUT_ID":
      return {
        ...state,
        ocrInputID: action.payload,
      };

    case "PREPARE_TARIC_DATA":
      const {
        selectedSenders = [],
        selectedReceivers = [],
        selectedAgents = [],
        userData,
        extractedData,
        company,
      } = action.payload || {};

      // Validate extractedData with fallbacks
      let validExtractedData = null;
      
      if (extractedData) {
        if (Array.isArray(extractedData)) {
          // Filter out null/undefined/empty objects
          const filtered = extractedData.filter(d => 
            d !== null && 
            d !== undefined && 
            typeof d === 'object' && 
            Object.keys(d).length > 0
          );
          if (filtered.length > 0) {
            validExtractedData = filtered;
          }
        } else if (typeof extractedData === 'object' && Object.keys(extractedData).length > 0) {
          validExtractedData = [extractedData];
        }
      }
      
      // Fallback: Use state.extractedData if payload extractedData is invalid
      if (!validExtractedData || validExtractedData.length === 0) {
        console.warn('PREPARE_TARIC_DATA: Invalid extractedData in payload, trying state.extractedData');
        if (state.extractedData) {
          if (Array.isArray(state.extractedData) && state.extractedData.length > 0) {
            validExtractedData = state.extractedData;
          } else if (typeof state.extractedData === 'object' && Object.keys(state.extractedData).length > 0) {
            validExtractedData = [state.extractedData];
          }
        }
      }
      
      // If still no valid data, return current state
      if (!validExtractedData || validExtractedData.length === 0) {
        console.error('PREPARE_TARIC_DATA: No valid extractedData available');
        return state;
      }

      // Backward compatibility: if old format is used, convert to arrays
      const senders = Array.isArray(selectedSenders) ? selectedSenders : (action.payload?.selectedSender ? [action.payload.selectedSender] : []);
      const receivers = Array.isArray(selectedReceivers) ? selectedReceivers : (action.payload?.selectedReceiver ? [action.payload.selectedReceiver] : []);
      const agents = Array.isArray(selectedAgents) ? selectedAgents : (action.payload?.selectedAgent ? [action.payload.selectedAgent] : []);

      // Process the declarations data - use validated extractedData
      const declarations = validExtractedData;

      // Generate incremented expediente values starting from CF00001
      const generateExpediente = (index) => {
        const number = (index + 1).toString().padStart(5, '0');
        return `CF${number}`;
      };

      // Process declarations with incremented expediente values
      // Use arrays to get sender/receiver/agent for each declaration index
      // Filter out invalid declarations first
      const validDeclarations = declarations.filter(d => 
        d !== null && 
        d !== undefined && 
        typeof d === 'object' && 
        Object.keys(d).length > 0
      );
      
      if (validDeclarations.length === 0) {
        console.error('PREPARE_TARIC_DATA: No valid declarations after filtering');
        return state;
      }
      
      const processedDeclarations = validDeclarations.map((declaration, index) => {
        // Validate declaration structure
        if (!declaration || typeof declaration !== 'object') {
          console.warn(`PREPARE_TARIC_DATA: Invalid declaration at index ${index}`);
          return null;
        }
        
        const selectedSender = senders[index] || null;
        const selectedReceiver = receivers[index] || null;
        const selectedAgent = agents[index] || null;

        // Build cliente, remitente, agente for this declaration
        const remitente = selectedReceiver ? {
          id: selectedReceiver.id,
          nombre: selectedReceiver.nombre,
          eori: selectedReceiver.eori,
          nif: selectedReceiver.nif || "",
          identificacion_fiscal: selectedReceiver.nif || "",
          direccion: selectedReceiver.direccion,
          localidad: selectedReceiver.localidad,
          pais: selectedReceiver.pais,
          codigo_postal_espana: company.codigo_postal_espana || null,
          codigo_postal_cee: company.codigo_postal_cee || null,
          codigo_provincia: company.codigo_provincia || null,
          nombre_provincia: company.nombre_provincia || null,
          prefijo_telefono: company.prefijo_telefono || null,
          numero_telefono: company.numero_telefono || null,
          extension_telefono: company.extension_telefono || null,
          numero_fax: company.numero_fax || null,
          e_mail: company.correo_electronico || null,
          aduana_habitual: company.aduana_habitual || null,
          clave_declarante: company.clave_declarante || null,
          garantia: company.garantia || null,
          garantia_transito: company.garantia_transito || null,
          garantia_cabildo: company.garantia_cabildo || null,
          forma_pago: company.forma_pago || null,
          forma_pago2: company.forma_pago2 || null,
          recargo_equivalencia: company.recargo_equivalencia || null,
          cuenta_contable: company.cuenta_contable || null,
          plazo_cobro: company.plazo_cobro || null,
        } : null;

        const cliente = selectedSender ? {
          id: selectedSender.id,
          nombre: selectedSender.nombre,
          eori: selectedSender.eori,
          nif: selectedSender.nif || "",
          identificacion_fiscal: selectedSender.nif || "",
          direccion: selectedSender.direccion,
          localidad: selectedSender.localidad,
          pais: selectedSender.pais,
          codigo_postal: selectedSender.codigo_postal || null,
          nombre_provincia: selectedSender.nombre_provincia || null,
          prefijo_telefono: selectedSender.prefijo_telefono || null,
          numero_telefono: selectedSender.numero_telefono || null,
          extension_telefono: selectedSender.extension_telefono || null,
          numero_fax: selectedSender.numero_fax || null,
          e_mail: selectedSender.e_mail || null,
          proc_simplificado_canarias: selectedSender.proc_simplificado_canarias || null,
          certificado_oea: selectedSender.certificado_oea || null,
          fecha_certificado_oea: selectedSender.fecha_certificado_oea || null,
        } : null;

        const agente = selectedAgent ? {
          id: selectedAgent.id,
          nombre: selectedAgent.nombre,
          nif: selectedAgent.nif || "",
          eori: selectedAgent.eori,
          direccion: selectedAgent.direccion,
          localidad: selectedAgent.localidad,
          pais: selectedAgent.pais,
          codigo: selectedAgent.codigo || null,
          codigo_postal: selectedAgent.codigo_postal || null,
          codigo_provincia: selectedAgent.codigo_provincia || null,
          prefijo_telefono: selectedAgent.prefijo_telefono || null,
          numero_telefono: selectedAgent.numero_telefono || null,
          extension_telefono: selectedAgent.extension_telefono || null,
          numero_fax: selectedAgent.numero_fax || null,
          e_mail: selectedAgent.e_mail || null,
          aduana: selectedAgent.aduana || null,
          clave_declarante: selectedAgent.clave_declarante || null,
          codigo_expedidor_autorizado: selectedAgent.codigo_expedidor_autorizado || null,
          garantia: selectedAgent.garantia || null,
          garantia_cabildo: selectedAgent.garantia_cabildo || null,
        } : null;

        return {
          circuito: 1,
          expediente: declaration.expediente || generateExpediente(index), // Use existing expediente if available, otherwise generate
          tipo: declaration.tipo || 64,
          cliente_id: cliente?.id || null,
          agente_id: agente?.id || null,
          remitente_id: remitente?.id || null,
          _cliente: cliente,
          _remitente: remitente,
          _agente: agente,
          aduana_entrada: declaration.aduana_entrada || null,
          aduana_salida: declaration.aduana_salida || null,
          aduana_despacho: declaration.aduana_despacho || null,
          fecha_registro: declaration.fecha_registro || null,

          // Export-specific fields from the Taric JSON example
          pais_destino: declaration.pais_destino || null,
          pais_exportacion: declaration.pais_exportacion || null,
          modo_transporte_frontera:
            declaration.modo_transporte_frontera || null,
          modo_transporte_interior:
            declaration.modo_transporte_interior || null,
          identidad_transporte_frontera:
            declaration.identidad_transporte_frontera || null,
          pais_transporte_frontera:
            declaration.pais_transporte_frontera || null,

          // Commercial information fields
          tiene_contenedores: declaration.tiene_contenedores || null,
          total_bultos: declaration.total_bultos || null,
          factura: declaration.factura || null,
          fecha_factura: declaration.fecha_factura || null,
          condiciones_entrega: declaration.condiciones_entrega || null,
          naturaleza_transaccion: declaration.naturaleza_transaccion || null,

          // Additional fields
          declaracion_1: declaration.declaracion_1 || null,
          declaracion_2: declaration.declaracion_2 || null,
          declaracion: declaration.declaracion || null,
          fecha_registro: declaration.fecha_registro || null,
          fecha_creacion: declaration.fecha_creacion || null,
          observaciones: declaration.observaciones || null,
          estado_edi: declaration.estado_edi || null,
          // circuito: declaration.circuito || null,
          atr1: declaration.atr1 || null,
          eur1: declaration.eur1 || null,
          dv1: declaration.dv1 || null,

          // New declaration-level fields
          completo: declaration.completo ?? 1,
          version: declaration.version ?? 2,
          // nombre_usuario_ultima_modificacion: declaration.nombre_usuario_ultima_modificacion ?? "",
          es_plantilla: declaration.es_plantilla ?? 0,
          pais_ubicacion: declaration.pais_ubicacion ?? "ES",
          tipo_ubicacion: declaration.tipo_ubicacion ?? "B",
          localizacion: declaration.localizacion ?? "",
          divisa: declaration.divisa ?? "900",
          cambio_divisa: declaration.cambio_divisa ?? "1,0",
          es_simplificado: declaration.es_simplificado ?? 0,
          es_recapitulativo: declaration.es_recapitulativo ?? 0,
          autorizacion_por_operacion: declaration.autorizacion_por_operacion ?? 0,
          estado_pdc: declaration.estado_pdc ?? 0,
          e_mail: declaration.e_mail ?? "",
          contacto_telefono: declaration.contacto_telefono ?? "",
          divisa_iso: declaration.divisa_iso ?? "EUR",

          // Missing fields from PDF analysis (Page 1 - Expediente Tab)
          mrn: declaration.mrn || null, // Movement Reference Number - CRITICAL missing field
          fecha_operacion: declaration.fecha_operacion || null, // Operation Date
          clave_declarante: declaration.clave_declarante || company.clave_declarante || null, // Declarant Key (from declaration or company)

          // Missing fields from PDF analysis (Page 2 - Declaración Tab)
          lugar_entrega: declaration.lugar_entrega || null, // Delivery Place
          codigo_un: declaration.codigo_un || null, // UN Code for delivery place
          tipo_autorizacion_cd: declaration.tipo_autorizacion_cd || null, // Type under 49 Autorización C.D.
          identificacion_adicional: declaration.identificacion_adicional || null, // Additional Identification

          // Missing fields from PDF analysis (Page 3 - Datos Transporte Tab)
          medio_transporte_frontera: declaration.medio_transporte_frontera || null, // Transport Means (separate from modo_transporte_frontera)

          // Missing fields from PDF analysis (Page 4 - Documentos/Autoriz. Tab)
          documentos_transporte: declaration.documentos_transporte || [], // Transport Documents (N730, etc.) - declaration level

          // Missing fields from PDF analysis (Page 5 - Información Adicional Tab)
          referencias_adicionales: declaration.referencias_adicionales || [], // Additional References (Y024, etc.)
          informacion_adicional: declaration.informacion_adicional || [], // Additional Information (I5007, etc.)

          // Missing fields from PDF analysis (Page 6 - Importes Tab)
          flag_datos_seguridad: declaration.flag_datos_seguridad ?? 0, // Security Data Flag (0 = No contiene datos de seguridad)

          // Declaration-level documentos (root level of expediente)
          // If documentos property exists in declaration (even if empty), use it; otherwise set to empty array
          // NOTE: Do NOT auto-aggregate here - let the component handle aggregation when toggle is enabled
          documentos: (() => {
            // Check if documentos property exists in the declaration object
            // If it exists (even if empty array), respect the user's choice
            if ('documentos' in declaration) {
              if (Array.isArray(declaration.documentos)) {
                // User has explicitly set documentos (could be empty array if toggle is off)
                return declaration.documentos
                  .filter(doc => doc !== null && doc !== undefined && typeof doc === 'object')
                  .map(doc => ({
                    codigo_documento: doc.codigo_documento || null,
                    tipo: doc.tipo !== undefined && doc.tipo !== null && doc.tipo !== "" ? Number(doc.tipo) : null,
                    numero: doc.numero || null,
                    fecha: doc.fecha || null,
                  }));
              }
              // If documentos exists but is not an array, return empty array
              return [];
            }
            
            // documentos property doesn't exist - set to empty array (don't auto-aggregate)
            // The component will handle aggregation when the toggle is enabled
            return [];
          })(),

          // Items (partidas) - validate and filter
          partidas:
            (declaration.partidas && Array.isArray(declaration.partidas) ? declaration.partidas : [])
              .filter(item => item !== null && item !== undefined && typeof item === 'object')
              .map((item) => {
              // Convert provincia nombre to codigo if needed
              let provinciaOrigen = item.provincia_origen || null;
              if (provinciaOrigen) {
                const provincias = getProvincias();
                const foundProvincia = provincias.find(
                  (p) => p.codigo === provinciaOrigen || 
                         p.nombre === provinciaOrigen ||
                         (p.nombre?.toLowerCase() === provinciaOrigen.toLowerCase()) ||
                         (p.codigo?.toString() === provinciaOrigen.toString())
                );
                if (foundProvincia) {
                  provinciaOrigen = foundProvincia.codigo;
                }
              }
              
              // Validate item structure
              if (!item || typeof item !== 'object') {
                console.warn('PREPARE_TARIC_DATA: Invalid item in partidas, skipping');
                return null;
              }
              
              return {
                codigo_nc: item.codigo_nc || null,
                descripcion_mercancia:
                  item.descripcion_mercancia || item.descripcion || "",
                masa_bruta: item.masa_bruta || null,
                masa_neta: item.masa_neta || null,
                pais_origen: item.pais_origen || remitente?.pais || "ES",
                provincia_origen: provinciaOrigen,

              // Financial information
              precio: item.precio || null,
              cantidad_euros: item.cantidad_euros || null,
              valor_estadistico: item.valor_estadistico || null,
              cantidad_divisas: item.cantidad_divisas || null,

              // Tariff information
              regimen_solicitado_precedente:
                item.regimen_solicitado_precedente ||
                item.regimen_aduanero ||
                "10.00",
              regimen_comunitario_nacional:
                item.regimen_comunitario_nacional !== undefined && item.regimen_comunitario_nacional !== null
                  ? item.regimen_comunitario_nacional
                  : null, // Explicit null when empty (not undefined)

              // Missing fields from PDF analysis (Page 9-13 - Partida Details)
              observaciones: item.observaciones || null, // Observations per partida (specific text like "SOIVRE\nEFITCAN")

              // New fields from table
              adiciones: item.adiciones || "0",
              deducciones: item.deducciones || "0",
              observaciones_arancelarias: item.observaciones_arancelarias || 512,
              observaciones_arancelarias_2: item.observaciones_arancelarias_2 || 0,
              deducciones_base_iva: item.deducciones_base_iva || "0",

              // Documents and packaging from the Taric JSON example - validate arrays
              documentos: (Array.isArray(item.documentos) ? item.documentos : [])
                .filter(doc => doc !== null && doc !== undefined)
                .map(doc => ({
                  ...doc,
                  tipo: doc.tipo !== undefined && doc.tipo !== null && doc.tipo !== "" ? Number(doc.tipo) : null
                })),
              embalajes: Array.isArray(item.embalajes) ? item.embalajes : [],
              };
            }).filter(item => item !== null), // Remove any null entries from invalid items
        };
      }).filter(decl => decl !== null); // Remove any null entries from invalid declarations
      
      // Final validation: ensure we have valid processed declarations
      if (!processedDeclarations || processedDeclarations.length === 0) {
        console.error('PREPARE_TARIC_DATA: No valid processed declarations after processing');
        return state;
      }

      // Extract clientes, remitentes, agentes from all declarations (one per export, allowing duplicates)
      const allClientes = processedDeclarations.map(d => d?._cliente).filter(Boolean);
      const allRemitentes = processedDeclarations.map(d => d?._remitente).filter(Boolean);
      const allAgentes = processedDeclarations.map(d => d?._agente).filter(Boolean);
  
      // Clean up temporary fields
      const cleanedDeclarations = processedDeclarations.map(({ _cliente, _remitente, _agente, ...decl }) => decl);

      return {
        ...state,
        cliente: allClientes.length > 0 ? allClientes[0] : null, // Keep first for backward compatibility
        remitente: allRemitentes.length > 0 ? allRemitentes[0] : null,
        agente: allAgentes.length > 0 ? allAgentes[0] : null,
        clientes: allClientes, // Array with one entry per export (allowing duplicates)
        remitentes: allRemitentes, // Array with one entry per export (allowing duplicates)
        agentes: allAgentes, // Array with one entry per export (allowing duplicates)
        userData,
        declarations: cleanedDeclarations,
        editableData: cleanedDeclarations,
        isReady: true,
      };

    case "GENERATE_TARIC_JSON":
      // Validate state before generating JSON
      if (!state.declarations || !Array.isArray(state.declarations) || state.declarations.length === 0) {
        console.error('GENERATE_TARIC_JSON: No valid declarations available');
        return state;
      }
      
      const userName =
        state.userData
          ? `${state.userData?.email}`
          : "default.user";
          
      // Validate and filter declarations
      const validDeclarationsForJson = state.declarations.filter(d => 
        d !== null && 
        d !== undefined && 
        typeof d === 'object' && 
        Object.keys(d).length > 0
      );
      
      if (validDeclarationsForJson.length === 0) {
        console.error('GENERATE_TARIC_JSON: No valid declarations after filtering');
        return state;
      }

      const taricJson = {
        ocr_input_id: (state.ocrInputID || "").toString(),
        taric: {
          // Use arrays if available, otherwise fall back to single values for backward compatibility
          clientes: (state.clientes && Array.isArray(state.clientes) && state.clientes.length > 0) 
            ? state.clientes.filter(c => c !== null && c !== undefined)
            : (state.cliente && typeof state.cliente === 'object' ? [state.cliente] : []),
          agentes: (state.agentes && Array.isArray(state.agentes) && state.agentes.length > 0) 
            ? state.agentes.filter(a => a !== null && a !== undefined)
            : (state.agente && typeof state.agente === 'object' ? [state.agente] : []),
          remitentes: (state.remitentes && Array.isArray(state.remitentes) && state.remitentes.length > 0) 
            ? state.remitentes.filter(r => r !== null && r !== undefined)
            : (state.remitente && typeof state.remitente === 'object' ? [state.remitente] : []),
          duas: {
            exportaciones:
              validDeclarationsForJson.map((declaration, idx) => {
                // Validate declaration before processing
                if (!declaration || typeof declaration !== 'object') {
                  console.warn(`GENERATE_TARIC_JSON: Invalid declaration at index ${idx}, skipping`);
                  return null;
                }
                
                return {
                id: idx + 1, // Unique, incrementing integer
                version: 2,
                completo: declaration.completo ?? 1,
                // nombre_usuario_ultima_modificacion: userName,
                es_plantilla: declaration.es_plantilla ?? 0,
                pais_ubicacion: declaration.pais_ubicacion || "ES",
                tipo_ubicacion: declaration.tipo_ubicacion || "B",
                localizacion: declaration.localizacion || "",
                divisa: declaration.divisa || "900",
                cambio_divisa: declaration.cambio_divisa || "1,0",
                es_simplificado: declaration.es_simplificado || 0,
                es_recapitulativo: declaration.es_recapitulativo || 0,
                autorizacion_por_operacion: declaration.autorizacion_por_operacion || 0,
                estado_pdc: declaration.estado_pdc || 0,
                e_mail: declaration.e_mail || userName,
                contacto_telefono: declaration.contacto_telefono || "",
                remitente_id: declaration.remitente_id || null,
                cliente_id: declaration.cliente_id || null,
                divisa_iso: declaration.divisa_iso || "EUR",
                // Basic Declaration Information
                expediente: declaration.expediente,
                tipo: declaration.tipo,
                remitente_id: declaration.remitente_id || null,
                cliente_id: declaration.cliente_id || null,
                agente_id: declaration.agente_id || null,
                // Customs and Transport Information
                aduana_salida: declaration.aduana_salida,
                aduana_despacho: declaration.aduana_despacho,
                pais_destino: declaration.pais_destino,
                pais_exportacion: declaration.pais_exportacion,
                modo_transporte_frontera: declaration.modo_transporte_frontera,
                modo_transporte_interior: declaration.modo_transporte_interior,
                identidad_transporte_frontera:
                  declaration.identidad_transporte_frontera,
                pais_transporte_frontera: declaration.pais_transporte_frontera,
                // Commercial information
                tiene_contenedores: declaration.tiene_contenedores,
                total_bultos: declaration.total_bultos,
                factura: declaration.factura,
                fecha_factura: declaration.fecha_factura,
                condiciones_entrega: declaration.condiciones_entrega,
                naturaleza_transaccion: declaration.naturaleza_transaccion,
                // Additional fields
                declaracion_1: declaration.declaracion_1 || null,
                declaracion_2: declaration.declaracion_2 || null,
                declaracion: declaration.declaracion || null,
                //fecha_registro: declaration.fecha_registro || null,
                fecha_creacion: declaration.fecha_creacion || null,
                observaciones: declaration.observaciones || null,
                estado_edi: declaration.estado_edi || null,
                //circuito: declaration.circuito || null,

                // Missing fields from PDF analysis - Declaration level
                mrn: declaration.mrn || null, // Movement Reference Number - CRITICAL
                fecha_operacion: declaration.fecha_operacion || null, // Operation Date
                clave_declarante: declaration.clave_declarante || null, // Declarant Key
                lugar_entrega: declaration.lugar_entrega || null, // Delivery Place
                codigo_un: declaration.codigo_un || null, // UN Code for delivery place
                tipo_autorizacion_cd: declaration.tipo_autorizacion_cd || null, // Type under 49 Autorización C.D.
                identificacion_adicional: declaration.identificacion_adicional || null, // Additional Identification
                medio_transporte_frontera: declaration.medio_transporte_frontera || null, // Transport Means
                documentos_transporte: declaration.documentos_transporte || [], // Transport Documents (N730, etc.)
                referencias_adicionales: declaration.referencias_adicionales || [], // Additional References (Y024, etc.)
                informacion_adicional: declaration.informacion_adicional || [], // Additional Information (I5007, etc.)
                flag_datos_seguridad: declaration.flag_datos_seguridad ?? 0, // Security Data Flag

                // Declaration-level documentos (root level of expediente)
                // If documentos property exists in declaration (even if empty), use it; otherwise set to empty array
                // NOTE: Do NOT auto-aggregate here - let the component handle aggregation when toggle is enabled
                documentos: (() => {
                  // Check if documentos property exists in the declaration object
                  // If it exists (even if empty array), respect the user's choice
                  if ('documentos' in declaration) {
                    if (Array.isArray(declaration.documentos)) {
                      // User has explicitly set documentos (could be empty array if toggle is off)
                      return declaration.documentos
                        .filter(doc => doc !== null && doc !== undefined && typeof doc === 'object')
                        .map(doc => ({
                          codigo_documento: doc.codigo_documento || null,
                          tipo: doc.tipo !== undefined && doc.tipo !== null && doc.tipo !== "" ? Number(doc.tipo) : null,
                          numero: doc.numero || null,
                          fecha: doc.fecha || null,
                        }));
                    }
                    // If documentos exists but is not an array, return empty array
                    return [];
                  }
                  
                  // documentos property doesn't exist - set to empty array (don't auto-aggregate)
                  // The component will handle aggregation when the toggle is enabled
                  return [];
                })(),

                // Items list - validate and filter
                partidas: (Array.isArray(declaration.partidas) ? declaration.partidas : [])
                  .filter(p => p !== null && p !== undefined && typeof p === 'object'),
              };
              }).filter(exp => exp !== null), // Remove any null entries from invalid declarations
          },
        },
      };

      return {
        ...state,
        taricJson,
      };

    case "SET_SUBMISSION_STATUS":
      return {
        ...state,
        isSubmitted: action.payload.isSubmitted,
        submissionError: action.payload.submissionError,
      };

    case "RESET_PREPARED_JSON":
      return initialPreparedJsonState;

    case "SET_TARIC_JSON_FROM_GROUPED":
      // Set taricJson directly from grouped API response
      return {
        ...state,
        taricJson: action.payload.taricJson,
        ocrInputID: action.payload.ocr_input_id || "",
        selectedExportacionesIndex: action.payload.exportacionesIndex || null,
      };
    
    case "SET_SELECTED_EXPORTACIONES_INDEX":
      return {
        ...state,
        selectedExportacionesIndex: action.payload,
      };

    case "SET_SUCCESSFUL_EXPEDIENTES":
      return {
        ...state,
        successfulExpedientes: action.payload,
      };

    case "SET_ERROR_EXPEDIENTES":
      return {
        ...state,
        errorExpedientes: action.payload,
      };

    case "CLEAR_EXPEDIENTES":
      return {
        ...state,
        successfulExpedientes: [],
        errorExpedientes: [],
      };

    default:
      return state;
  }
};

export const SET_COMBINED_DOCUMENT = "SET_COMBINED_DOCUMENT";

// Action Creators
export const setCombinedDocument = (isCombined) => ({
  type: SET_COMBINED_DOCUMENT,
  payload: isCombined,
});

// Action creators for prepared JSON
export const prepareTaricData = (data) => ({
  type: "PREPARE_TARIC_DATA",
  payload: data,
});

export const setTaricJsonErrors = (errors) => ({
  type: "SET_TARIC_JSON_ERRORS",
  payload: errors,
});

export const setSuccessfulExpedientes = (expedientes) => ({
  type: "SET_SUCCESSFUL_EXPEDIENTES",
  payload: expedientes,
});

export const setErrorExpedientes = (expedientes) => ({
  type: "SET_ERROR_EXPEDIENTES",
  payload: expedientes,
});

export const clearExpedientes = () => ({
  type: "CLEAR_EXPEDIENTES",
});

export const filterDeclarationsByErrors = (errorIndices) => ({
  type: "FILTER_DECLARATIONS_BY_ERRORS",
  payload: errorIndices,
});

export const clearTaricJsonErrors = () => ({
  type: "CLEAR_TARIC_JSON_ERRORS",
});

export const generateTaricJson = () => ({
  type: "GENERATE_TARIC_JSON",
});

export const setJsonSubmissionStatus = (status) => ({
  type: "SET_SUBMISSION_STATUS",
  payload: status,
});

export const resetPreparedJson = () => ({
  type: "RESET_PREPARED_JSON",
});

export const setOcrInputID = (id) => ({
  type: "SET_OCR_INPUT_ID",
  payload: id,
});

export const setSelectedExportacionesIndex = (index) => ({
  type: "SET_SELECTED_EXPORTACIONES_INDEX",
  payload: index,
});

// Action Creator
export const toggleCollapseFiles = () => ({
  type: TOGGLE_COLLAPSE_FILES,
});

// Add new action creators for validation errors
export const setValidationErrors = (errors, isSpanish) => ({
  type: "SET_VALIDATION_ERRORS",
  payload: { errors, isSpanish }
});

export const clearValidationErrors = () => ({
  type: "CLEAR_VALIDATION_ERRORS"
});

export const setValidationLanguage = (isSpanish) => ({
  type: "SET_VALIDATION_LANGUAGE",
  payload: isSpanish
});

// Add selected declaration reducer
const selectedDeclarationReducer = (state = null, action) => {
  switch (action.type) {
    case SET_SELECTED_DECLARATION:
      return action.payload;
    case CLEAR_SELECTED_DECLARATION:
      return null;
    default:
      return state;
  }
};

// Add action creators for selected declaration
export const setSelectedDeclaration = (declaration) => ({
  type: SET_SELECTED_DECLARATION,
  payload: declaration,
});

export const clearSelectedDeclaration = () => ({
  type: CLEAR_SELECTED_DECLARATION,
});

const initialCreditStatsState = {
  current: null,
  usage: null,
  exportCost: null,
  loading: false,
  error: null,
};

// Credit stats reducer
const creditStatsReducer = (state = initialCreditStatsState, action) => {
  switch (action.type) {
    case 'SET_CREDIT_STATS':
      return {
        ...state,
        ...action.payload,
        loading: false,
        error: null,
      };
    case 'SET_CREDIT_STATS_LOADING':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'SET_CREDIT_STATS_ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case 'CLEAR_CREDIT_STATS':
      return initialCreditStatsState;
    default:
      return state;
  }
};

// Credit stats actions
export const setCreditStats = (data) => ({
  type: 'SET_CREDIT_STATS',
  payload: data,
});

export const setCreditStatsLoading = () => ({
  type: 'SET_CREDIT_STATS_LOADING',
});

export const setCreditStatsError = (error) => ({
  type: 'SET_CREDIT_STATS_ERROR',
  payload: error,
});

export const clearCreditStats = () => ({
  type: 'CLEAR_CREDIT_STATS',
});

// Thunk to fetch credit stats
export const fetchCreditStats = () => async (dispatch) => {
  dispatch(setCreditStatsLoading());
  try {
    const API_BASE_URL = process.env.REACT_APP_DOCS_API_KEY;
    // Import apiFetch dynamically to avoid circular dependencies
    const { default: apiFetch } = await import('../../utils/api');
    const [currentRes, usageRes, exportCostRes] = await Promise.all([
      apiFetch(`https://${API_BASE_URL}/api/credits/current`, {
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json'
        }
      }),
      apiFetch(`https://${API_BASE_URL}/api/credits/usage/today`, {
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json'
        }
      }),
      apiFetch(`https://${API_BASE_URL}/api/credits/export-cost`, {
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json'
        }
      })
    ]);
    
    if (!currentRes.ok || !usageRes.ok || !exportCostRes.ok) {
      const errorDetails = {
        current: currentRes.status,
        usage: usageRes.status,
        exportCost: exportCostRes.status
      };
      throw new Error(`Failed to fetch credit stats: ${JSON.stringify(errorDetails)}`);
    }
    
    const [current, usage, exportCost] = await Promise.all([
      currentRes.json(),
      usageRes.json(),
      exportCostRes.json()
    ]);
    
    dispatch(setCreditStats({ current, usage, exportCost }));
  } catch (err) {
    dispatch(setCreditStatsError(err.message));
  }
};

// Stripe credit products
const initialProductsState = [
  {
    credits: "10,000",
    price: "€499.00",
    productId: "prod_10000_credits"
  },
  {
    credits: "5,000",
    price: "€299.00",
    productId: "prod_5000_credits"
  },
  {
    credits: "1,000",
    price: "€59.00",
    productId: "prod_1000_credits"
  },
  {
    credits: "100",
    price: "€9.99",
    productId: "prod_100_credits"
  },
];

export const productsReducer = (state = initialProductsState, action) => {
  // This is a static list, but you could add actions to update if needed
  return state;
};

// Suggestions reducer
const initialSuggestionsState = {
  suggestions: [], // Always use array, never null
  suggestionJobId: null,
};

export const suggestionsReducer = (state = initialSuggestionsState, action) => {
  switch (action.type) {
    case "SET_SUGGESTIONS":
      // Validate payload
      if (!action.payload) {
        console.warn('SET_SUGGESTIONS: Invalid payload (null/undefined)');
        return state;
      }
      
      // Validate and normalize suggestions
      let validSuggestions = null;
      const { suggestions, suggestionJobId } = action.payload;
      
      if (suggestions !== null && suggestions !== undefined) {
        if (Array.isArray(suggestions)) {
          // Filter out null/undefined/empty objects
          const filtered = suggestions.filter(s => 
            s !== null && 
            s !== undefined && 
            typeof s === 'object' && 
            (s.exportacion_index !== undefined || s.agents || s.senders || s.receivers)
          );
          validSuggestions = filtered.length > 0 ? filtered : [];
        } else if (typeof suggestions === 'object') {
          // Single suggestion object, convert to array
          validSuggestions = [suggestions];
        } else {
          console.warn('SET_SUGGESTIONS: Invalid suggestions type:', typeof suggestions);
          validSuggestions = [];
        }
      } else {
        // null or undefined - store as empty array for consistency
        validSuggestions = [];
      }
      
      console.log('SET_SUGGESTIONS reducer - storing suggestions:', {
        count: validSuggestions?.length || 0,
        suggestionJobId: suggestionJobId || null,
        suggestions: validSuggestions
      });
      
      return {
        suggestions: validSuggestions,
        suggestionJobId: suggestionJobId || null,
      };
    case "CLEAR_SUGGESTIONS":
      return {
        suggestions: [], // Always return array
        suggestionJobId: null,
      };
    default:
      return state;
  }
};

// Action creators for suggestions
export const SET_SUGGESTIONS = "SET_SUGGESTIONS";
export const CLEAR_SUGGESTIONS = "CLEAR_SUGGESTIONS";

export const setSuggestions = (suggestions, suggestionJobId) => ({
  type: SET_SUGGESTIONS,
  payload: { suggestions, suggestionJobId },
});

export const clearSuggestions = () => ({
  type: CLEAR_SUGGESTIONS,
});

// Templates reducer with localStorage persistence
const TEMPLATES_STORAGE_KEY = 'taric_templates';

// Load templates from localStorage on initialization
const loadTemplatesFromStorage = () => {
  try {
    const stored = localStorage.getItem(TEMPLATES_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate that parsed data is an array
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      } else if (Array.isArray(parsed)) {
        // Empty array is valid, return it
        return parsed;
      } else {
        console.warn('Templates in localStorage are not an array, using defaults');
      }
    }
  } catch (error) {
    console.error('Error loading templates from localStorage:', error);
    // Clear corrupted data
    try {
      localStorage.removeItem(TEMPLATES_STORAGE_KEY);
    } catch (e) {
      console.error('Error clearing corrupted templates from localStorage:', e);
    }
  }
  // Default templates if none exist
  return [
    {
      id: 'almeria',
      title: 'Almeria',
      color: '#2DD0D6',
      data: {
        regimen_solicitud: '10',
        declaracion_1: 'EX',
        declaracion_2: 'A',
        naturaleza_transaccion: '11',
        telefono: '948403026',
        contacto: 'ADUANAS@GDAR.ES',
        marcas: 'RTDAS',
        medio_transporte: '30',
      },
    },
    {
      id: 'iberveg',
      title: 'IBERVEG',
      color: '#10B981',
      data: {
        aduana_despacho: '0441',
        pais_exportacion: 'ES',
        provincia: 'AL',
        localizacion_mercancia: '0441LAME05',
        documentos: [
          { tipo: 'N380', descripcion: 'DOC SUPLEMENTARIOS FUERA DE LA PARTIDA' },
          { tipo: 'N730', descripcion: 'DOC DE TRANSPORTE FUERA DE LA PARTIDA' },
          { tipo: 'Y024', descripcion: 'REFERENCIAS ADICIONALES FUERA DE LA PARTIDA' },
          { tipo: 'I5007', descripcion: 'INFORMACION ADICIONAL FUERA DE LA PARTIDA' },
          { tipo: 'N003', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
          { tipo: '1217', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
          { tipo: '1009', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
        ],
      },
    },
    {
      id: 'actiture',
      title: 'ACTITURE',
      color: '#F59E0B',
      data: {
        aduana_despacho: '3161',
        aduana_salida: 'FR000740',
        pais_exportacion: 'ES',
        provincia: 'NA',
        localizacion_mercancia: '3161K30007',
        documentos: [
          { tipo: 'N380', descripcion: 'DOC SUPLEMENTARIOS FUERA DE LA PARTIDA' },
          { tipo: 'N730', descripcion: 'DOC DE TRANSPORTE FUERA DE LA PARTIDA' },
          { tipo: 'Y024', descripcion: 'REFERENCIAS ADICIONALES FUERA DE LA PARTIDA' },
          { tipo: 'N003', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
          { tipo: '1217', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
        ],
      },
    },
    {
      id: 'zanobeet',
      title: 'ZANOBEET',
      color: '#8B5CF6',
      data: {
        aduana_despacho: '3161',
        pais_exportacion: 'ES',
        provincia: 'CA',
        localizacion_mercancia: '3161K30007',
        documentos: [
          { tipo: 'N380', descripcion: 'DOC SUPLEMENTARIOS FUERA DE LA PARTIDA' },
          { tipo: 'N730', descripcion: 'DOC DE TRANSPORTE FUERA DE LA PARTIDA' },
          { tipo: 'Y024', descripcion: 'REFERENCIAS ADICIONALES FUERA DE LA PARTIDA' },
          { tipo: 'N003', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
          { tipo: '1217', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
        ],
      },
    },
    {
      id: 'cebollas',
      title: 'CEBOLLAS',
      color: '#126FEF',
      data: {
        aduana_despacho: '3161',
        pais_exportacion: 'ES',
        provincia: 'NA',
        localizacion_mercancia: '3161K30007',
        documentos: [
          { tipo: 'N380', descripcion: 'DOC SUPLEMENTARIOS FUERA DE LA PARTIDA' },
          { tipo: 'N730', descripcion: 'DOC DE TRANSPORTE FUERA DE LA PARTIDA' },
          { tipo: 'Y024', descripcion: 'REFERENCIAS ADICIONALES FUERA DE LA PARTIDA' },
          { tipo: 'N003', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
          { tipo: '1217', descripcion: 'DOC SUPLEMENTARIOS DENTRO DE LA PARTIDA' },
        ],
      },
    },
  ];
};

// Save templates to localStorage
const saveTemplatesToStorage = (templates) => {
  try {
    // Validate templates is an array before saving
    if (!Array.isArray(templates)) {
      console.error('Cannot save templates: not an array', templates);
      return;
    }
    localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify(templates));
  } catch (error) {
    console.error('Error saving templates to localStorage:', error);
    // Handle quota exceeded error
    if (error.name === 'QuotaExceededError') {
      console.error('localStorage quota exceeded. Consider clearing old data.');
    }
  }
};

const initialTemplatesState = loadTemplatesFromStorage();

export const templatesReducer = (state = initialTemplatesState, action) => {
  // Ensure state is always an array
  const currentState = Array.isArray(state) ? state : initialTemplatesState;
  let newState;
  
  switch (action.type) {
    case 'SET_TEMPLATES':
      // Validate payload is an array
      if (!Array.isArray(action.payload)) {
        console.warn('SET_TEMPLATES: payload is not an array, ignoring');
        return currentState;
      }
      newState = action.payload;
      saveTemplatesToStorage(newState);
      return newState;
    
    case 'ADD_TEMPLATE':
      // Validate payload exists and has required fields
      if (!action.payload || !action.payload.id) {
        console.warn('ADD_TEMPLATE: invalid payload, ignoring');
        return currentState;
      }
      // Check if template with same id already exists
      if (currentState.some(t => t.id === action.payload.id)) {
        console.warn(`ADD_TEMPLATE: template with id ${action.payload.id} already exists, ignoring`);
        return currentState;
      }
      newState = [...currentState, action.payload];
      saveTemplatesToStorage(newState);
      return newState;
    
    case 'UPDATE_TEMPLATE':
      // Validate payload exists and has id
      if (!action.payload || !action.payload.id) {
        console.warn('UPDATE_TEMPLATE: invalid payload, ignoring');
        return currentState;
      }
      // Check if template exists
      const templateExists = currentState.some(t => t.id === action.payload.id);
      if (!templateExists) {
        console.warn(`UPDATE_TEMPLATE: template with id ${action.payload.id} not found, ignoring`);
        return currentState;
      }
      newState = currentState.map(template => 
        template.id === action.payload.id ? action.payload : template
      );
      saveTemplatesToStorage(newState);
      return newState;
    
    case 'DELETE_TEMPLATE':
      // Validate payload is a valid id
      if (!action.payload) {
        console.warn('DELETE_TEMPLATE: invalid payload, ignoring');
        return currentState;
      }
      newState = currentState.filter(template => template.id !== action.payload);
      saveTemplatesToStorage(newState);
      return newState;
    
    default:
      return currentState;
  }
};

// Action creators
export const setTemplates = (templates) => ({
  type: 'SET_TEMPLATES',
  payload: templates,
});

export const addTemplate = (template) => ({
  type: 'ADD_TEMPLATE',
  payload: template,
});

export const updateTemplate = (template) => ({
  type: 'UPDATE_TEMPLATE',
  payload: template,
});

export const deleteTemplate = (templateId) => ({
  type: 'DELETE_TEMPLATE',
  payload: templateId,
});

const rootReducer = combineReducers({
  user: userReducer,
  agents: agentsReducer,
  senders: sendersReducer,
  receivers: receiversReducer,
  documents: documentsReducer,
  ui: uiReducer,
  preparedJson: preparedJsonReducer,
  company: companyReducer,
  validationErrors: validationErrorsReducer,
  selectedDeclaration: selectedDeclarationReducer,
  creditStats: creditStatsReducer,
  products: productsReducer,
  groupedTaricJsonOutputs: groupedTaricJsonOutputsReducer,
  suggestions: suggestionsReducer,
  templates: templatesReducer,
});

export default rootReducer;
